import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileSpreadsheet, Users, BarChart3, Trash2, LogOut,
  Menu, X, CalendarDays, Image, Activity, Sun, Moon, Settings as SettingsIcon
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import AdGallery from "./AdGallery";
import DealsTab from "./tabs/DealsTab";
import UsersTab from "./tabs/UsersTab";
import ReportingTab from "./tabs/ReportingTab";
import FormLogsTab from "./tabs/FormLogsTab";
import EventsTab from "./tabs/EventsTab";
import ServiceTokensTab from "./tabs/ServiceTokensTab";
import PendingJobsIndicator from "../components/PendingJobsIndicator";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Tab section mapping: each tab unlocks when ANY of its sections has non-none access.
const TAB_SECTIONS = {
  deals: ["deals"],
  users: ["users"],
  campaigns: ["campaigns", "marketplace_reporting"], // "campaigns" tab == Reporting page
  events: ["events_rsvps"],
  gallery: ["ad_gallery"],
  logs: ["logs"],
  settings: ["settings"],
};

const hasAccess = (perms, sections) => sections.some(s => perms?.[s] && perms[s] !== "none");

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { light, toggle: toggleTheme } = useTheme();
  const token = localStorage.getItem("adminToken");
  const userRole = localStorage.getItem("adminRole");
  const userName = localStorage.getItem("adminName");

  const isSuperAdmin = userRole === "super_admin";

  const [permissions, setPermissions] = useState(null);
  const [permsLoading, setPermsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, name: "", onConfirm: null });

  // Fetch current user's role-permissions matrix on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me/permissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPermissions(res.data.permissions || {}))
      .catch(() => setPermissions({})) // fail-safe: empty = no tabs
      .finally(() => setPermsLoading(false));
  }, [token]);

  const visibleTabs = useMemo(() => {
    if (!permissions) return [];
    return [
      { id: "deals", label: "Deals", icon: FileSpreadsheet },
      { id: "users", label: "Users", icon: Users },
      { id: "campaigns", label: "Reporting", icon: BarChart3 },
      { id: "events", label: "Events", icon: CalendarDays },
      { id: "gallery", label: "Ad Gallery", icon: Image },
      { id: "logs", label: "Logs", icon: Activity },
      { id: "settings", label: "Settings", icon: SettingsIcon },
    ].filter(t => hasAccess(permissions, TAB_SECTIONS[t.id]));
  }, [permissions]);

  // Pick a default active tab once permissions resolve
  useEffect(() => {
    if (permissions && !activeTab && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [permissions, activeTab, visibleTabs]);

  const requestDelete = useCallback((type, id, name, onConfirm) => {
    setDeleteConfirm({ open: true, type, id, name, onConfirm });
  }, []);

  const confirmDelete = () => {
    if (deleteConfirm.onConfirm) deleteConfirm.onConfirm();
    setDeleteConfirm({ open: false, name: "", onConfirm: null });
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const getDeleteDialogContent = () => {
    const n = deleteConfirm.name;
    const typeMap = {
      deal: { title: "Delete Deal?", description: `This will permanently delete the deal for "${n}".`, actionLabel: "Delete Deal" },
      user: { title: "Delete User?", description: `This will permanently delete the user "${n}" and cannot be undone.`, actionLabel: "Delete User" },
      campaign: { title: "Delete Campaign?", description: `This will permanently delete the campaign "${n}" and all its data.`, actionLabel: "Delete Campaign" },
      formlog: { title: "Delete Form Log?", description: `This will permanently delete this form log entry.`, actionLabel: "Delete Log" },
      rsvp: { title: "Delete Guest?", description: `This will permanently remove "${n}" from the guest list.`, actionLabel: "Remove Guest" },
      activitylog: { title: "Delete Log?", description: `This will permanently delete this activity log entry.`, actionLabel: "Delete Log" },
      marketplace_upload: { title: "Delete Upload?", description: `This will permanently delete "${n}" and all its normalized data.`, actionLabel: "Delete Upload" },
    };
    return typeMap[deleteConfirm.type] || { title: "Delete?", description: "Are you sure?", actionLabel: "Delete" };
  };

  // Helpers children can use for action-level gating
  const can = (section, level = "read") => {
    const v = permissions?.[section] || "none";
    const order = { none: 0, read: 1, edit: 2 };
    return order[v] >= order[level];
  };

  if (permsLoading || !permissions) {
    return (
      <div className="min-h-screen bg-[#141617] flex items-center justify-center">
        <div className="text-[#ebebeb]/60 font-mono text-sm" data-testid="dashboard-loading">Loading dashboard…</div>
      </div>
    );
  }

  if (visibleTabs.length === 0) {
    return (
      <div className="min-h-screen bg-[#141617] flex items-center justify-center p-6">
        <div className="text-center max-w-md" data-testid="no-access-empty-state">
          <h2 className="text-white font-mono text-xl mb-2">No sections available</h2>
          <p className="text-[#ebebeb]/60 font-mono text-sm mb-6">Your current role doesn't grant access to any section. Please contact a Super Admin.</p>
          <button onClick={handleLogout} className="ingenium-button" data-testid="no-access-logout-btn">Logout</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141617] flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-[#191b1d] border-r border-white/10 flex flex-col transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b border-white/10 relative">
          {/* Mobile close button — absolute so it doesn't break centering */}
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden absolute top-3 right-3 text-[#ebebeb]/60" data-testid="sidebar-close-btn">
            <X className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center text-center gap-2">
            <img
              src={light ? "/logos/ingenium-light.png" : "/logos/ingenium-dark.png"}
              alt="Ingenium"
              className="h-10 w-auto object-contain"
              data-testid="sidebar-logo"
            />
            <p className={`${light ? "text-gray-500" : "text-[#ebebeb]/50"} text-[11px] font-mono leading-tight`}>
              Empowering Digital Experiences
            </p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {visibleTabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-mono text-sm transition-colors ${activeTab === tab.id ? 'bg-[#ff3737]/10 text-[#ff3737]' : 'text-[#ebebeb]/60 hover:bg-white/5'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          {isSuperAdmin && (
            <div className="mb-3">
              <PendingJobsIndicator
                token={token}
                onClick={() => setActiveTab("campaigns")}
              />
            </div>
          )}
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 rounded-full bg-[#ff3737]/20 flex items-center justify-center">
              <span className="text-[#ff3737] font-mono text-sm font-bold">{(userName || "A")[0].toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-mono truncate">{userName || "Admin"}</p>
              <p className="text-[#ebebeb]/40 text-xs font-mono truncate">{userRole?.replace("_", " ")}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-mono text-sm text-[#ebebeb]/60 hover:bg-white/5 transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
          <button onClick={toggleTheme} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-mono text-sm text-[#ebebeb]/60 hover:bg-white/5 transition-colors" data-testid="theme-toggle-global">
            {light ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            {light ? "Dark Mode" : "Light Mode"}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-[#141617]/95 backdrop-blur-sm border-b border-white/10 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="text-[#ebebeb]/60 hover:text-white"><Menu className="w-6 h-6" /></button>
          <img
            src={light ? "/logos/ingenium-light.png" : "/logos/ingenium-dark.png"}
            alt="Ingenium"
            className="h-6 w-auto"
            data-testid="mobile-logo"
          />
        </header>

        {/* Mobile Tab Bar */}
        <div className="lg:hidden flex overflow-x-auto gap-2 px-4 py-3 border-b border-white/10 bg-[#191b1d]">
          {visibleTabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-[#ff3737]/15 text-[#ff3737]' : 'text-[#ebebeb]/50 hover:text-[#ebebeb]/80'}`}
              data-testid={`mobile-tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          ))}
        </div>

        <main className="p-4 lg:p-8 max-w-[1400px]">
          {activeTab === "deals" && can("deals", "read") && (
            <DealsTab
              token={token}
              isSuperAdmin={isSuperAdmin}
              canEdit={can("deals", "edit")}
              requestDelete={requestDelete}
            />
          )}
          {activeTab === "users" && can("users", "read") && (
            <UsersTab token={token} requestDelete={requestDelete} canEdit={can("users", "edit")} />
          )}
          {activeTab === "campaigns" && (can("marketplace_reporting", "read") || can("campaigns", "read")) && (
            <ReportingTab
              token={token}
              requestDelete={requestDelete}
              userRole={userRole}
              isSuperAdmin={isSuperAdmin}
              canViewCampaigns={can("campaigns", "read")}
              canEditCampaigns={can("campaigns", "edit")}
              canViewMarketplace={can("marketplace_reporting", "read")}
            />
          )}
          {activeTab === "logs" && can("logs", "read") && (
            <FormLogsTab token={token} requestDelete={requestDelete} canEdit={can("logs", "edit")} />
          )}
          {activeTab === "events" && can("events_rsvps", "read") && (
            <EventsTab
              token={token}
              isSuperAdmin={isSuperAdmin}
              requestDelete={requestDelete}
              canEdit={can("events_rsvps", "edit")}
            />
          )}
          {activeTab === "gallery" && can("ad_gallery", "read") && (
            <AdGallery mode="admin" canEdit={can("ad_gallery", "edit")} />
          )}
          {activeTab === "settings" && can("settings", "read") && (
            <ServiceTokensTab token={token} />
          )}
        </main>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm(prev => ({ ...prev, open: false }))}>
        <AlertDialogContent className="bg-[#1a1a1a] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#ebebeb] flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              {deleteConfirm.open && getDeleteDialogContent().title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#ebebeb]/60">
              {deleteConfirm.open && getDeleteDialogContent().description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-[#ebebeb] hover:bg-white/5 hover:text-[#ebebeb]" data-testid="delete-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-confirm-action">
              {deleteConfirm.open && getDeleteDialogContent().actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
